import { useMemo } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { ProfileFilter } from '../components/ProfileFilter';
import { DashPanel } from '../components/DashPanel';
import { DataCardHeader } from '../components/CardExportButton';
import { RankedList } from '../components/RankedList';
import { exportCardCsv } from '../utils/csvExport';
import {
  BarChartHorizontal,
  ColumnChart,
} from '../components/charts/DashCharts';
import { HourClockChart, GroupedColumnChart } from '../components/charts/Heatmap';
import { computeRealRoutinesAnalytics, FIXED_APP_ROUTINES_COUNT } from '../utils/realRoutinesAnalytics';
import type { RoutineDetailRow, SubRoutineDetailRow } from '../types/routines';
import { cn } from '@/lib/utils';

function AlarmBadge({ hasAlarm }: { hasAlarm: boolean }) {
  return (
    <span
      className={cn(
        'text-[11px] font-medium px-1.5 py-0.5 rounded-full',
        hasAlarm
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
          : 'bg-muted text-muted-foreground'
      )}
    >
      {hasAlarm ? 'Com alarme' : 'Sem alarme'}
    </span>
  );
}

function RoutineDetailCard({
  title,
  rows,
  onExport,
}: {
  title: string;
  rows: RoutineDetailRow[];
  onExport?: () => void;
}) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="max-h-[320px] overflow-y-auto pr-1 scroll-styled">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wide bg-muted">
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Rotina</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Horário</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Alarme</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Dia(s) da semana</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} className="border-t border-border">
                <td className="py-2 px-2 text-foreground">{row.name}</td>
                <td className="py-2 px-2 text-muted-foreground tabular-nums">{row.typicalHour}</td>
                <td className="py-2 px-2">
                  <AlarmBadge hasAlarm={row.hasAlarm} />
                </td>
                <td className="py-2 px-2 text-muted-foreground">{row.weekdays}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashPanel>
  );
}

function SubRoutineDetailCard({
  title,
  rows,
  onExport,
}: {
  title: string;
  rows: SubRoutineDetailRow[];
  onExport?: () => void;
}) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="max-h-[320px] overflow-y-auto pr-1 scroll-styled">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wide bg-muted">
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Subrotina</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Rotina</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Horário</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Alarme</th>
              <th className="py-1.5 px-2 first:rounded-l-md last:rounded-r-md">Dia(s) da semana</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row.name}-${idx}`} className="border-t border-border">
                <td className="py-2 px-2 text-foreground">{row.name}</td>
                <td className="py-2 px-2 text-muted-foreground">{row.routineName}</td>
                <td className="py-2 px-2 text-muted-foreground tabular-nums">{row.hour}</td>
                <td className="py-2 px-2">
                  <AlarmBadge hasAlarm={row.hasAlarm} />
                </td>
                <td className="py-2 px-2 text-muted-foreground">{row.weekdays}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashPanel>
  );
}

export function RoutinesModule() {
  const { filteredProfiles, profilesLoading } = useDashboard();

  const analytics = useMemo(() => computeRealRoutinesAnalytics(filteredProfiles), [filteredProfiles]);

  return (
    <div>
      <ProfileFilter />

      {!profilesLoading && filteredProfiles.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          Nenhum perfil encontrado para esse filtro no Firestore.
        </p>
      )}

      <div className="mb-6 pb-4 border-b border-border flex flex-wrap gap-8">
        <div>
          <p className="text-xs text-muted-foreground">Rotinas do app (catálogo)</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {FIXED_APP_ROUTINES_COUNT}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Rotinas com subrotinas criadas</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.routinesUsed}
          </p>
        </div>
      </div>

      <section className="mb-6">
        <RankedList
          title="Rotinas mais usadas (mais subrotinas criadas)"
          rows={analytics.rankedRoutines.map((r) => ({
            label: r.name,
            value: r.value.toLocaleString('pt-BR'),
          }))}
          onExport={() =>
            exportCardCsv(
              'rotinas-mais-usadas',
              ['Rotina', 'Subrotinas criadas'],
              analytics.rankedRoutines.map((r) => [r.name, r.value])
            )
          }
        />
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Detalhamento de rotinas e subrotinas
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RoutineDetailCard
            title="Rotinas"
            rows={analytics.routineDetails}
            onExport={() =>
              exportCardCsv(
                'rotinas-detalhamento',
                ['Rotina', 'Horário', 'Alarme', 'Dia(s) da semana'],
                analytics.routineDetails.map((r) => [
                  r.name,
                  r.typicalHour,
                  r.hasAlarm ? 'Com alarme' : 'Sem alarme',
                  r.weekdays,
                ])
              )
            }
          />
          <SubRoutineDetailCard
            title="Subrotinas"
            rows={analytics.subRoutineDetails}
            onExport={() =>
              exportCardCsv(
                'subrotinas-detalhamento',
                ['Subrotina', 'Rotina', 'Horário', 'Alarme', 'Dia(s) da semana'],
                analytics.subRoutineDetails.map((r) => [
                  r.name,
                  r.routineName,
                  r.hour,
                  r.hasAlarm ? 'Com alarme' : 'Sem alarme',
                  r.weekdays,
                ])
              )
            }
          />
        </div>
      </section>

      {/* Distribuição por dia e por horário lado a lado — ambos têm largura própria */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Uso por dia da semana e por horário
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ColumnChart
            title="Subrotinas criadas por dia (1 = segunda ... 7 = domingo)"
            data={analytics.byWeekday.map((w) => ({ day: w.label, count: w.count }))}
            nameKey="day"
            valueKey="count"
            onExport={() =>
              exportCardCsv(
                'rotinas-dia-semana',
                ['Dia', 'Criações'],
                analytics.byWeekday.map((w) => [w.label, w.count])
              )
            }
          />
          <HourClockChart
            title="Rotinas cadastradas por faixa horária"
            data={analytics.byHourBlock}
            onExport={() =>
              exportCardCsv(
                'rotinas-heatmap-horario',
                ['Horário', 'Criações'],
                analytics.byHourBlock.map((b) => [b.label, b.count])
              )
            }
          />
        </div>
      </section>

      {/* Dia da semana × rotina ao lado de alarmes — ambos têm largura própria */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Distribuição por rotina e alarmes
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GroupedColumnChart
            title="Dia da semana × rotina"
            data={analytics.weekdayByRoutine}
            nameKey="day"
            series={analytics.routineSeries}
            onExport={() =>
              exportCardCsv(
                'rotinas-dia-semana-detalhado',
                ['Dia', ...analytics.routineSeries.map((s) => s.label)],
                analytics.weekdayByRoutine.map((row) => [
                  row.day,
                  ...analytics.routineSeries.map((s) => row[s.key]),
                ])
              )
            }
          />
          <BarChartHorizontal
            title="Rotinas com mais alarmes configurados"
            data={analytics.alarmAdherence.map((r) => ({
              name: r.routineName,
              count: r.alarmCount,
            }))}
            nameKey="name"
            valueKey="count"
            onExport={() =>
              exportCardCsv(
                'rotinas-adesao-alarme',
                ['Rotina', 'Alarmes configurados'],
                analytics.alarmAdherence.map((r) => [r.routineName, r.alarmCount])
              )
            }
          />
        </div>
      </section>

      <section className="mb-2">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Repetição de alarmes por rotina
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankedList
            title="Mais subrotinas com alarme 1x na semana"
            rows={analytics.routinesByOnceAWeekAlarms.map((r) => ({
              label: r.name,
              value: r.value.toLocaleString('pt-BR'),
            }))}
            onExport={() =>
              exportCardCsv(
                'rotinas-alarme-1x-semana',
                ['Rotina', 'Subrotinas com alarme 1x/semana'],
                analytics.routinesByOnceAWeekAlarms.map((r) => [r.name, r.value])
              )
            }
          />
          <RankedList
            title="Mais subrotinas com alarme todo dia"
            rows={analytics.routinesByEverydayAlarms.map((r) => ({
              label: r.name,
              value: r.value.toLocaleString('pt-BR'),
            }))}
            onExport={() =>
              exportCardCsv(
                'rotinas-alarme-todo-dia',
                ['Rotina', 'Subrotinas com alarme todo dia'],
                analytics.routinesByEverydayAlarms.map((r) => [r.name, r.value])
              )
            }
          />
        </div>
      </section>
    </div>
  );
}
